import React, { useEffect, useState, useCallback } from 'react';
import { ncService } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
import Button from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import StateBlock from '../ui/StateBlock';
import UpsellPro from '../UpsellPro'; // #323 — upsell no lugar do outbound bloqueado por tier

/**
 * #64 Feature 3 (Sessão 3) — seção de convite a fornecedor no NCResult.
 *
 * - Lista convites existentes com status (Enviado/Aberto/Respondido/Revogado/Expirado)
 * - Form pra convidar (admin+operador) — backend gera token + email + retorna URL
 * - Botão copiar link (caso email não chegue) + revogar
 * - Mostra resposta do fornecedor quando respondido
 *
 * Auditor (read-only) vê a lista mas não convida/revoga (backend já força 403).
 */

const STATUS_LABEL = {
  enviado: { txt: 'Enviado', cls: 'sup-badge-enviado' },
  aberto: { txt: 'Aberto pelo fornecedor', cls: 'sup-badge-aberto' },
  respondido: { txt: 'Respondido', cls: 'sup-badge-respondido' },
  revogado: { txt: 'Revogado', cls: 'sup-badge-revogado' },
  expirado: { txt: 'Expirado', cls: 'sup-badge-expirado' },
};

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function NCResultSupplierInvite({ ncId, user, nc, onPulled }) {
  const role = user?.role || '';
  const podeConvidar = role === 'admin' || role === 'operador';
  // #323 — NC-para-fornecedor (outbound) é perk Pro+. Sinal read-only do /auth/me
  // (default true se ausente; o gate autoritativo é o 403 do backend em
  // POST /{nc_id}/invite-supplier). false → substitui a seção pelo upsell. Com
  // GATING_FEATURES_HABILITADO OFF, o backend devolve true ⇒ inerte.
  const outboundLiberado = user?.outbound_liberado !== false;
  // #275 F1 — nota: o "puxar" de ação usa shape DUPLA (aliases what↔descricao
  // etc.), então funciona igual nos 2 modos da NC sem branch por modo aqui.

  const [convites, setConvites] = useState([]);
  // #275 F1 — modal de confirmação do puxar: {titulo, corpo, onConfirm}
  const [puxar, setPuxar] = useState(null);
  const [puxando, setPuxando] = useState(false);
  // #64 outbound S5: nome do fornecedor da NC (entidade) pra pré-preencher
  // o campo "Nome/empresa" do convite e evitar redigitação.
  const [fornecedorSugerido, setFornecedorSugerido] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [instrucao, setInstrucao] = useState(''); // #64 S4
  const [enviando, setEnviando] = useState(false);
  const [revogandoId, setRevogandoId] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      // #64 outbound S5: response virou envelope {convites, fornecedor_nome_sugerido}
      const data = await ncService.listarConvitesFornecedor(ncId);
      setConvites(data.convites || []);
      setFornecedorSugerido(data.fornecedor_nome_sugerido || '');
    } catch (err) {
      showError(`Erro ao carregar convites: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [ncId]);

  // #64 outbound S5: ao abrir o form, pré-preenche "Nome/empresa" com o
  // fornecedor da NC (editável). Só se o campo ainda estiver vazio.
  const abrirForm = () => {
    if (!nome.trim() && fornecedorSugerido) setNome(fornecedorSugerido);
    setShowForm(true);
  };

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleConvidar = async (e) => {
    e.preventDefault();
    if (!email.trim() || enviando) return;
    setEnviando(true);
    try {
      const criado = await ncService.convidarFornecedor(ncId, {
        supplier_email: email.trim(),
        supplier_nome: nome.trim() || null,
        mensagem_instrucao: instrucao.trim() || null,
      });
      showSuccess('Convite criado e e-mail enviado ao fornecedor.');
      // Copia o link automaticamente pro clipboard (fallback se email falhar)
      if (criado.public_url) {
        try {
          await navigator.clipboard.writeText(criado.public_url);
          showSuccess('Link copiado para a área de transferência.');
        } catch {
          // clipboard pode falhar sem HTTPS/permissão — não crítico
        }
      }
      setEmail('');
      setNome('');
      setInstrucao('');
      setShowForm(false);
      await carregar();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        showError('Apenas administradores e operadores podem convidar fornecedores.');
      } else {
        showError(`Erro ao convidar: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setEnviando(false);
    }
  };

  const handleRevogar = async (inviteId) => {
    if (revogandoId) return;
    setRevogandoId(inviteId);
    try {
      await ncService.revogarConviteFornecedor(ncId, inviteId);
      showSuccess('Convite revogado. O link parou de funcionar.');
      await carregar();
    } catch (err) {
      showError(`Erro ao revogar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRevogandoId(null);
    }
  };

  const ativo = (c) => c.status === 'enviado' || c.status === 'aberto';

  // #275 F1 — "puxar pra análise": o tenant PUXA por campo (1 clique), com
  // atribuição na trilha (tipo_correcao='puxado_fornecedor'). NUNCA
  // automático — a resposta do fornecedor é proposta a revisar (fronteira
  // da verdade do plano #275 §2/§5).
  const executarPuxar = async (edicoes, nomeFornecedor, msgOk) => {
    setPuxando(true);
    try {
      await ncService.editarAnalise(ncId, edicoes, { atribuicao: nomeFornecedor || 'fornecedor' });
      showSuccess(msgOk);
      setPuxar(null);
      if (onPulled) onPulled();
    } catch (err) {
      showError(`Não foi possível aplicar na análise: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPuxando(false);
    }
  };

  const pedirPuxarCausa = (c, causa) => setPuxar({
    titulo: 'Usar a causa do fornecedor na análise?',
    corpo: `A causa raiz da análise será substituída por: "${causa}". A trilha registra a proveniência (fornecido por ${c.supplier_nome || c.supplier_email}).`,
    onConfirm: () => executarPuxar(
      [{ campo: 'causa_raiz.declaracao', valor: causa }],
      c.supplier_nome || c.supplier_email,
      'Causa raiz atualizada com a resposta do fornecedor.'
    ),
  });

  const pedirPuxarAcao = (c, acao, preventiva = false) => {
    // Shape dupla (aliases what↔descricao etc.): qualquer consumidor
    // (editor/relatório/cron) acha a chave que procura, nos 2 modos.
    const valor = {
      what: acao.o_que, descricao: acao.o_que,
      who: acao.quem || '', responsavel_funcao: acao.quem || '',
      when: acao.quando || '', prazo: acao.quando || '',
      why: '', prioridade: 'Média', status: 'Pendente',
    };
    const arr = preventiva ? 'acoes_preventivas' : 'acoes_corretivas';
    setPuxar({
      titulo: `Adicionar esta ação ${preventiva ? 'preventiva' : 'corretiva'} à análise?`,
      corpo: `"${acao.o_que}" será adicionada às ações da análise, atribuída a ${c.supplier_nome || c.supplier_email} na trilha.`,
      onConfirm: () => executarPuxar(
        [{ campo: `${arr}.+`, valor }],
        c.supplier_nome || c.supplier_email,
        'Ação adicionada à análise.'
      ),
    });
  };

  const pedirPuxarContencao = (c, est) => {
    const partes = [est.acao_contencao];
    if (est.contencao_prazo) partes.push(`Prazo: ${est.contencao_prazo}`);
    const edicoes = [{ campo: 'acao_contencao_imediata.descricao', valor: partes.join(' — ') }];
    if (est.contencao_responsavel) {
      edicoes.push({ campo: 'acao_contencao_imediata.responsavel_funcao', valor: est.contencao_responsavel });
    }
    setPuxar({
      titulo: 'Usar a contenção do fornecedor na análise?',
      corpo: `A ação de contenção da análise será preenchida com a resposta do fornecedor, atribuída a ${c.supplier_nome || c.supplier_email} na trilha.`,
      onConfirm: () => executarPuxar(
        edicoes,
        c.supplier_nome || c.supplier_email,
        'Contenção atualizada com a resposta do fornecedor.'
      ),
    });
  };

  const CONF_BADGE = {
    concordo: { txt: 'Concorda com o problema', cls: 'sup-conf-ok' },
    concordo_parcial: { txt: 'Concorda parcialmente', cls: 'sup-conf-parcial' },
    contesto: { txt: 'Contesta o problema', cls: 'sup-conf-contesta' },
  };

  // #323 — tier abaixo do Pro: substitui a seção inteira pelo upsell (uma conta
  // Starter nunca teve convites, então não há lista a preservar). Após todos os
  // hooks pra respeitar as Rules of Hooks.
  if (!outboundLiberado) {
    return (
      <UpsellPro
        icone="factory"
        titulo="NC para fornecedor"
        descricao="Envie um link para o fornecedor responder sobre a NC — contenção, causa e ações — sem precisar de login, e puxe a resposta direto para a análise. Ideal para tratar não conformidades da sua cadeia de suprimentos."
      />
    );
  }

  return (
    <section className="result-section sup-invite-section">
      <h3><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>factory</span>Resposta de fornecedor</h3>
      <p className="sup-help">
        Envie um link pro fornecedor responder sobre esta NC sem precisar de
        login. Ele vê apenas o problema e a causa raiz — não os dados internos.
      </p>

      {loading ? (
        <StateBlock kind="loading" message="Carregando convites…" />
      ) : convites.length === 0 ? (
        <StateBlock kind="empty" icon="mail" message="Nenhum fornecedor convidado ainda." />
      ) : (
        <div className="sup-lista">
          {convites.map((c) => {
            const st = STATUS_LABEL[c.status] || { txt: c.status, cls: '' };
            return (
              <div key={c.id} className="sup-item">
                <div className="sup-item-head">
                  <span className="sup-email">{c.supplier_nome || c.supplier_email}</span>
                  <span className={`sup-badge ${st.cls}`}>{st.txt}</span>
                </div>
                <div className="sup-item-meta">
                  {c.created_at && <span>Convidado: {fmt(c.created_at)}</span>}
                  {c.responded_at && <span>Respondeu: {fmt(c.responded_at)}</span>}
                  {c.expires_at && c.status === 'enviado' && (
                    <span>Expira: {fmt(c.expires_at)}</span>
                  )}
                </div>
                {(() => {
                  // #275 F1 — painel de revisão: resposta COMPLETA + "puxar
                  // pra análise" por campo. Fonte canônica: resposta_estruturada;
                  // campos planos antigos seguem como fallback (respostas legadas).
                  const est = c.resposta_estruturada || {};
                  const causa = est.causa_identificada || c.causa_identificada;
                  const contencao = est.acao_contencao || c.acao_contencao;
                  const conf = CONF_BADGE[est.confirmacao];
                  const temResposta = causa || contencao || c.acao_corretiva || c.prazo
                    || c.resposta_texto || est.confirmacao
                    || (est.porques || []).length > 0
                    || (est.acoes_corretivas || []).length > 0
                    || (est.acoes_preventivas || []).length > 0;
                  if (!temResposta) return null;
                  const podePuxar = podeConvidar;
                  const btnPuxar = (onClick, rotulo) => podePuxar && (
                    <Button type="button" variant="secondary" size="sm" icon="south_west"
                      className="sup-btn-puxar" onClick={onClick} disabled={puxando}>
                      {rotulo}
                    </Button>
                  );
                  return (
                    <div className="sup-resposta">
                      <label>Resposta do fornecedor</label>
                      {conf && (
                        <p>
                          <span className={`sup-conf-badge ${conf.cls}`}>{conf.txt}</span>
                          {est.confirmacao_comentario && <span> — {est.confirmacao_comentario}</span>}
                        </p>
                      )}
                      {contencao && (
                        <div className="sup-resp-campo">
                          <p><strong>Contenção:</strong> {contencao}
                            {est.contencao_prazo ? ` · Prazo: ${est.contencao_prazo}` : ''}
                            {est.contencao_responsavel ? ` · Responsável: ${est.contencao_responsavel}` : ''}</p>
                          {est.acao_contencao && btnPuxar(() => pedirPuxarContencao(c, est), 'Usar na análise')}
                        </div>
                      )}
                      {causa && (
                        <div className="sup-resp-campo">
                          <p><strong>Causa identificada:</strong> {causa}</p>
                          {btnPuxar(() => pedirPuxarCausa(c, causa), 'Usar como causa raiz')}
                        </div>
                      )}
                      {Array.isArray(est.porques) && est.porques.length > 0 && (
                        <div className="sup-resp-campo">
                          <p><strong>Cadeia de porquês do fornecedor:</strong></p>
                          {est.porques.map((p, i) => (
                            <p key={i} className="sup-porque">{i + 1}. {p}</p>
                          ))}
                        </div>
                      )}
                      {Array.isArray(est.acoes_corretivas) && est.acoes_corretivas.map((a, i) => (
                        <div key={`ac-${i}`} className="sup-resp-campo">
                          <p><strong>Ação corretiva {i + 1}:</strong> {a.o_que}
                            {a.quem ? ` — ${a.quem}` : ''}{a.quando ? ` (${a.quando})` : ''}</p>
                          {btnPuxar(() => pedirPuxarAcao(c, a, false), 'Adicionar à análise')}
                        </div>
                      ))}
                      {Array.isArray(est.acoes_preventivas) && est.acoes_preventivas.map((a, i) => (
                        <div key={`ap-${i}`} className="sup-resp-campo">
                          <p><strong>Ação preventiva {i + 1}:</strong> {a.o_que}
                            {a.quem ? ` — ${a.quem}` : ''}{a.quando ? ` (${a.quando})` : ''}</p>
                          {btnPuxar(() => pedirPuxarAcao(c, a, true), 'Adicionar à análise')}
                        </div>
                      ))}
                      {c.acao_corretiva && !((est.acoes_corretivas || []).length) && (
                        <p><strong>Ação corretiva:</strong> {c.acao_corretiva}</p>
                      )}
                      {c.prazo && (
                        <p><strong>Prazo:</strong> {c.prazo}</p>
                      )}
                      {c.resposta_texto && (
                        <p><strong>Observações:</strong> {c.resposta_texto}</p>
                      )}
                      {podePuxar && (
                        <p className="sup-puxar-hint">
                          "Usar/Adicionar" leva o campo pra análise da NC com
                          atribuição ao fornecedor na trilha — nada entra
                          automaticamente.
                        </p>
                      )}
                    </div>
                  );
                })()}
                {podeConvidar && ativo(c) && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="sup-btn-revogar"
                    onClick={() => handleRevogar(c.id)}
                    disabled={revogandoId === c.id}
                  >
                    {revogandoId === c.id ? '…' : 'Revogar link'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {podeConvidar && (
        <div className="sup-acoes">
          {!showForm ? (
            <Button type="button" variant="primary" icon="add" onClick={abrirForm}>
              Convidar fornecedor
            </Button>
          ) : (
            <form className="sup-form" onSubmit={handleConvidar}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-mail do fornecedor *"
                required
                disabled={enviando}
              />
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome / empresa (opcional)"
                disabled={enviando}
              />
              {/* #64 S4 — instrução do tenant ao fornecedor. Placeholder de
                  exemplo educa sobre o uso sem obrigar. */}
              <textarea
                value={instrucao}
                onChange={(e) => setInstrucao(e.target.value)}
                placeholder="Instruções pro fornecedor (opcional). Ex: Por favor descreva (1) a causa do seu lado, (2) a ação corretiva que pretende tomar, (3) o prazo de implementação."
                rows={3}
                maxLength={500}
                disabled={enviando}
                className="sup-form-instrucao"
              />
              <div className="sup-form-botoes">
                <Button type="submit" variant="primary" icon="send" disabled={enviando || !email.trim()}>
                  {enviando ? 'Enviando…' : 'Enviar convite'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setShowForm(false); setEmail(''); setNome(''); setInstrucao(''); }}
                  disabled={enviando}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* #275 F1 — confirmação do "puxar pra análise" (nunca automático) */}
      <ConfirmModal
        open={!!puxar}
        title={puxar?.titulo || ''}
        icon="south_west"
        confirmLabel={puxando ? 'Aplicando…' : 'Aplicar na análise'}
        loading={puxando}
        onConfirm={() => puxar?.onConfirm()}
        onCancel={() => setPuxar(null)}
      >
        <p>{puxar?.corpo}</p>
      </ConfirmModal>
    </section>
  );
}

export default NCResultSupplierInvite;
